import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { Bus, MapPin, Users, CreditCard, Warning, Plus, CaretRight, Wrench } from '@phosphor-icons/react';
import './transport.css';

function TransportDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    total_vehicles: 0,
    active_routes: 0,
    enrolled_students: 0,
    fee_collection: { collected: 0, pending: 0 },
    vehicles_in_maintenance: 0
  });

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied: Admin role required');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        const [vehiclesData, routesData, enrollmentsData, feesData] = await Promise.all([
          transportService.listVehicles().catch(() => []),
          transportService.listRoutes().catch(() => []),
          transportService.listEnrollments().catch(() => []),
          transportService.listFees().catch(() => [])
        ]);

        const vehicles = Array.isArray(vehiclesData) ? vehiclesData : vehiclesData.vehicles || [];
        const routes = Array.isArray(routesData) ? routesData : routesData.routes || [];
        const enrollments = Array.isArray(enrollmentsData) ? enrollmentsData : enrollmentsData.enrollments || [];
        const fees = Array.isArray(feesData) ? feesData : feesData.fees || [];

        const activeVehicles = vehicles.filter(v => v.status === 'active');
        const maintenanceVehicles = vehicles.filter(v => v.status === 'maintenance');
        const activeRoutes = routes.filter(r => r.status === 'active');
        const paidFees = fees.filter(f => f.is_paid).reduce((sum, f) => sum + (f.amount_paid || 0), 0);
        const outstandingFees = fees.filter(f => !f.is_paid).reduce((sum, f) => sum + (f.amount_due || 0), 0);

        setMetrics({
          total_vehicles: activeVehicles.length,
          active_routes: activeRoutes.length,
          enrolled_students: enrollments.length || 0,
          fee_collection: { collected: paidFees, pending: outstandingFees },
          vehicles_in_maintenance: maintenanceVehicles.length
        });
      } catch (error) {
        console.error('Error loading metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, []);

  const MetricCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className={`metric-card metric-${color}`}>
      <div className="metric-icon">
        <Icon size={28} weight="duotone" />
      </div>
      <div className="metric-content">
        <h3>{title}</h3>
        <p className="metric-value">{value}</p>
        {subtitle && <p className="metric-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return <Layout><div className="loading">Loading transport dashboard...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page transport-dashboard">
        <div className="page-header">
          <div>
            <h1>Transport Management</h1>
            <p>Manage vehicles, routes, student enrollment, and fees</p>
          </div>
        </div>

        <div className="metrics-grid">
          <MetricCard
            icon={Bus}
            title="Vehicles"
            value={metrics.total_vehicles}
            subtitle={`${metrics.vehicles_in_maintenance} in maintenance`}
            color="primary"
          />
          <MetricCard
            icon={MapPin}
            title="Active Routes"
            value={metrics.active_routes}
            subtitle="All operational"
            color="secondary"
          />
          <MetricCard
            icon={Users}
            title="Enrolled Students"
            value={metrics.enrolled_students}
            subtitle="Current term"
            color="accent"
          />
          <MetricCard
            icon={CreditCard}
            title="Fee Collection"
            value={`GHS ${metrics.fee_collection.collected.toFixed(2)}`}
            subtitle={`GHS ${metrics.fee_collection.pending.toFixed(2)} outstanding`}
            color="success"
          />
        </div>

        <div className="section quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-grid">
            <div className="action-card" onClick={() => navigate('/transport/vehicles')}>
              <Bus size={32} />
              <h3>Manage Vehicles</h3>
              <p>Add, edit, or view vehicle details</p>
              <CaretRight size={20} />
            </div>
            <div className="action-card" onClick={() => navigate('/transport/routes')}>
              <MapPin size={32} />
              <h3>Manage Routes</h3>
              <p>Create and manage transport routes</p>
              <CaretRight size={20} />
            </div>
            <div className="action-card" onClick={() => navigate('/transport/enrollment')}>
              <Users size={32} />
              <h3>Student Enrollment</h3>
              <p>Enroll students in transport routes</p>
              <CaretRight size={20} />
            </div>
            <div className="action-card" onClick={() => navigate('/transport/attendance')}>
              <Warning size={32} />
              <h3>Mark Attendance</h3>
              <p>Daily attendance tracking</p>
              <CaretRight size={20} />
            </div>
            <div className="action-card" onClick={() => navigate('/transport/fees')}>
              <CreditCard size={32} />
              <h3>Manage Fees</h3>
              <p>Track and collect transport fees</p>
              <CaretRight size={20} />
            </div>
            <div className="action-card" onClick={() => navigate('/transport/drivers')}>
              <Bus size={32} />
              <h3>Drivers & Staff</h3>
              <p>Manage drivers and conductors</p>
              <CaretRight size={20} />
            </div>
          </div>
        </div>

        {metrics.vehicles_in_maintenance > 0 && (
          <div className="alert alert-warning">
            <Warning size={20} />
            <span>{metrics.vehicles_in_maintenance} vehicle(s) in maintenance. Check maintenance schedule.</span>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TransportDashboardPage;
